import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft,
  Edit,
  Clock,
  CheckCircle2,
  XCircle,
  Send,
  User,
  Calendar,
  Shield,
  FileText,
  Building,
  Key,
  AlertCircle,
  Ban,
  Settings,
  Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn, formatDateTime, formatDate } from '@/lib/utils';
import api from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import ApprovalModal from '../components/ApprovalModal';
import RejectModal from '../components/RejectModal';
import ImplementModal from '../components/ImplementModal';
import RevokeModal from '../components/RevokeModal';

// Access Form Status Colors
function getAccessFormStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-800',
    PENDING: 'bg-yellow-100 text-yellow-800',
    PENDING_HR: 'bg-yellow-100 text-yellow-800',
    PENDING_IT: 'bg-blue-100 text-blue-800',
    APPROVED: 'bg-green-100 text-green-800',
    IMPLEMENTED: 'bg-emerald-100 text-emerald-800',
    REJECTED: 'bg-red-100 text-red-800',
    REVOKED: 'bg-orange-100 text-orange-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

// Workflow Step Component
interface WorkflowStepProps {
  step: number;
  title: string;
  description: string;
  status: 'completed' | 'current' | 'pending' | 'rejected';
  approverName?: string;
  approvedAt?: string;
  isLast?: boolean;
}

function WorkflowStep({ step, title, description, status, approverName, approvedAt, isLast }: WorkflowStepProps) {
  const getStatusStyles = () => {
    switch (status) {
      case 'completed':
        return {
          circle: 'bg-green-500 text-white',
          line: 'bg-green-500',
          text: 'text-green-700',
        };
      case 'current':
        return {
          circle: 'bg-blue-500 text-white animate-pulse',
          line: 'bg-gray-200',
          text: 'text-blue-700',
        };
      case 'rejected':
        return {
          circle: 'bg-red-500 text-white',
          line: 'bg-red-500',
          text: 'text-red-700',
        };
      default:
        return {
          circle: 'bg-gray-200 text-gray-500',
          line: 'bg-gray-200',
          text: 'text-gray-500',
        };
    }
  };

  const styles = getStatusStyles();

  return (
    <div className="flex items-start">
      <div className="flex flex-col items-center">
        <div className={cn(
          "w-10 h-10 rounded-full flex items-center justify-center font-semibold",
          styles.circle
        )}>
          {status === 'completed' ? (
            <CheckCircle2 className="w-5 h-5" />
          ) : status === 'rejected' ? (
            <XCircle className="w-5 h-5" />
          ) : (
            step
          )}
        </div>
        {!isLast && (
          <div className={cn("w-0.5 h-16", styles.line)} />
        )}
      </div>
      <div className="ml-4 pb-8">
        <p className={cn("font-semibold", styles.text)}>{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
        {approverName && (
          <p className="text-xs text-muted-foreground mt-1">
            By: {approverName} {approvedAt && `• ${formatDateTime(approvedAt)}`}
          </p>
        )}
      </div>
    </div>
  );
}

// Workflow Visualization
function WorkflowVisualization({ form }: { form: any }) {
  const getStepStatus = (stepStatus: string): 'completed' | 'current' | 'pending' | 'rejected' => {
    const statusOrder = ['DRAFT', 'PENDING_HR', 'PENDING_IT', 'APPROVED', 'IMPLEMENTED'];
    const currentIndex = statusOrder.indexOf(form.status);
    const stepIndex = statusOrder.indexOf(stepStatus);

    if (form.status === 'REJECTED') {
      if (stepStatus === 'PENDING_HR' && !form.hrApproval) return 'rejected';
      if (stepStatus === 'PENDING_IT' && !form.itApproval) return 'rejected';
      if (stepIndex < currentIndex || form.hrApproval) return 'completed';
      return 'pending';
    }

    if (form.status === 'REVOKED') {
      return 'completed';
    }

    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'pending';
  };

  const steps = [
    {
      status: 'DRAFT',
      title: 'Draft Created',
      description: 'Access form created by line manager',
      approverName: form.lineManagerName,
      approvedAt: form.lineManagerSignedAt,
    },
    {
      status: 'PENDING_HR',
      title: 'HR Approval',
      description: 'Pending HR department review',
      approverName: form.hrApproverName,
      approvedAt: form.hrApprovedAt,
    },
    {
      status: 'PENDING_IT',
      title: 'IT Approval',
      description: 'Pending IT department review',
      approverName: form.itApproverName,
      approvedAt: form.itApprovedAt,
    },
    {
      status: 'APPROVED',
      title: 'Approved',
      description: 'All approvals completed',
    },
    {
      status: 'IMPLEMENTED',
      title: 'Implemented',
      description: 'Access has been provisioned',
      approverName: form.implementedBy?.firstName ? `${form.implementedBy.firstName} ${form.implementedBy.lastName}` : undefined,
      approvedAt: form.implementedAt,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Approval Workflow
        </CardTitle>
        <CardDescription>Track the progress of this access request</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="pl-2">
          {steps.map((step, index) => (
            <WorkflowStep
              key={step.status}
              step={index + 1}
              title={step.title}
              description={step.description}
              status={getStepStatus(step.status)}
              approverName={step.approverName}
              approvedAt={step.approvedAt}
              isLast={index === steps.length - 1}
            />
          ))}
        </div>

        {form.status === 'REVOKED' && (
          <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2 text-orange-700 font-medium">
              <Ban className="w-5 h-5" />
              Access Revoked
            </div>
            <p className="text-sm text-orange-600 mt-1">
              Revoked on {form.revokedAt ? formatDateTime(form.revokedAt) : 'N/A'}
            </p>
            {form.revokedReason && (
              <p className="text-sm text-orange-600 mt-1">
                Reason: {form.revokedReason}
              </p>
            )}
          </div>
        )}

        {form.status === 'REJECTED' && form.rejectionReason && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700 font-medium">
              <XCircle className="w-5 h-5" />
              Rejection Reason
            </div>
            <p className="text-sm text-red-600 mt-1">{form.rejectionReason}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Approval Details Card
function ApprovalDetailsCard({ form }: { form: any }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Approval Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Line Manager */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-muted-foreground">Line Manager</h4>
          <div className="p-3 bg-muted/50 rounded-lg">
            <p className="font-medium">{form.lineManagerName || 'N/A'}</p>
            {form.lineManagerSignedAt && (
              <p className="text-sm text-muted-foreground">
                Signed: {formatDateTime(form.lineManagerSignedAt)}
              </p>
            )}
            {form.lineManagerSignature && (
              <p className="text-xs text-muted-foreground mt-1 italic">
                Signature: {form.lineManagerSignature}
              </p>
            )}
          </div>
        </div>

        {/* HR Approval */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-muted-foreground">HR Approval</h4>
          <div className={cn(
            "p-3 rounded-lg",
            form.hrApproval === true ? "bg-green-50 border border-green-200" :
            form.hrApproval === false ? "bg-red-50 border border-red-200" :
            "bg-muted/50"
          )}>
            {form.hrApproverName ? (
              <>
                <div className="flex items-center gap-2">
                  {form.hrApproval === true && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  {form.hrApproval === false && <XCircle className="w-4 h-4 text-red-600" />}
                  <p className="font-medium">{form.hrApproverName}</p>
                </div>
                {form.hrApprovedAt && (
                  <p className="text-sm text-muted-foreground">
                    {form.hrApproval ? 'Approved' : 'Rejected'}: {formatDateTime(form.hrApprovedAt)}
                  </p>
                )}
                {form.hrComments && (
                  <p className="text-sm mt-2">{form.hrComments}</p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">Pending HR review</p>
            )}
          </div>
        </div>

        {/* IT Approval */}
        <div className="space-y-2">
          <h4 className="font-medium text-sm text-muted-foreground">IT Approval</h4>
          <div className={cn(
            "p-3 rounded-lg",
            form.itApproval === true ? "bg-green-50 border border-green-200" :
            form.itApproval === false ? "bg-red-50 border border-red-200" :
            "bg-muted/50"
          )}>
            {form.itApproverName ? (
              <>
                <div className="flex items-center gap-2">
                  {form.itApproval === true && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                  {form.itApproval === false && <XCircle className="w-4 h-4 text-red-600" />}
                  <p className="font-medium">{form.itApproverName}</p>
                </div>
                {form.itApprovedAt && (
                  <p className="text-sm text-muted-foreground">
                    {form.itApproval ? 'Approved' : 'Rejected'}: {formatDateTime(form.itApprovedAt)}
                  </p>
                )}
                {form.itComments && (
                  <p className="text-sm mt-2">{form.itComments}</p>
                )}
              </>
            ) : (
              <p className="text-muted-foreground">Pending IT review</p>
            )}
          </div>
        </div>

        {/* Implementation */}
        {(form.status === 'IMPLEMENTED' || form.implementedAt) && (
          <div className="space-y-2">
            <h4 className="font-medium text-sm text-muted-foreground">Implementation</h4>
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-emerald-600" />
                <p className="font-medium">
                  {form.implementedBy 
                    ? `${form.implementedBy.firstName} ${form.implementedBy.lastName}` 
                    : 'System'}
                </p>
              </div>
              {form.implementedAt && (
                <p className="text-sm text-muted-foreground">
                  Implemented: {formatDateTime(form.implementedAt)}
                </p>
              )}
              {form.implementationNotes && (
                <p className="text-sm mt-2">{form.implementationNotes}</p>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AccessFormDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const [showApprovalModal, setShowApprovalModal] = useState<'hr' | 'it' | null>(null);
  const [showRejectModal, setShowRejectModal] = useState<'hr' | 'it' | null>(null);
  const [showImplementModal, setShowImplementModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['accessForm', id],
    queryFn: async () => {
      const response = await api.get(`/access-forms/${id}`);
      return response.data.data;
    },
    enabled: Boolean(id),
  });

  const submitForHRMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/access-forms/${id}/submit-hr`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessForm', id] });
      refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete(`/access-forms/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accessForms'] });
      navigate('/access-forms');
    },
  });

  const form = data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!form) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-lg font-medium">Access form not found</p>
        <Button asChild className="mt-4">
          <Link to="/access-forms">Back to Access Forms</Link>
        </Button>
      </div>
    );
  }

  // Role-based permissions
  const userRole = user?.role?.toUpperCase() || '';
  const canSubmitForHR = form.status === 'DRAFT';
  const canApproveHR = form.status === 'PENDING_HR' && ['HR', 'ADMIN', 'SUPER_ADMIN'].includes(userRole);
  const canApproveIT = form.status === 'PENDING_IT' && ['IT', 'TECHNICIAN', 'ADMIN', 'SUPER_ADMIN'].includes(userRole);
  const canImplement = form.status === 'APPROVED' && ['IT', 'TECHNICIAN', 'ADMIN', 'SUPER_ADMIN'].includes(userRole);
  const canRevoke = form.status === 'IMPLEMENTED' && ['HR', 'IT', 'ADMIN', 'SUPER_ADMIN'].includes(userRole);
  const canEdit = form.status === 'DRAFT';
  const canDelete = form.status === 'DRAFT';

  // Parse systems requested
  let systemsRequested: string[] = [];
  try {
    systemsRequested = typeof form.systemsRequested === 'string' 
      ? JSON.parse(form.systemsRequested) 
      : form.systemsRequested || [];
  } catch {
    systemsRequested = [];
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/access-forms">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold">{form.formNumber}</h1>
              <span className={cn(
                "px-3 py-1 text-sm font-medium rounded-full",
                getAccessFormStatusColor(form.status)
              )}>
                {form.status.replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-muted-foreground">
              Created {formatDateTime(form.createdAt)}
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          {canEdit && (
            <Button variant="outline" asChild>
              <Link to={`/access-forms/${id}/edit`}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button 
              variant="outline" 
              className="text-red-600 hover:text-red-700"
              onClick={() => {
                if (confirm('Are you sure you want to delete this access form?')) {
                  deleteMutation.mutate();
                }
              }}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
          {canSubmitForHR && (
            <Button 
              onClick={() => submitForHRMutation.mutate()}
              disabled={submitForHRMutation.isPending}
            >
              <Send className="w-4 h-4 mr-2" />
              Submit for HR Approval
            </Button>
          )}
          {canApproveHR && (
            <>
              <Button 
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => setShowRejectModal('hr')}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button onClick={() => setShowApprovalModal('hr')}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve (HR)
              </Button>
            </>
          )}
          {canApproveIT && (
            <>
              <Button 
                variant="outline"
                className="text-red-600 hover:text-red-700"
                onClick={() => setShowRejectModal('it')}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
              <Button onClick={() => setShowApprovalModal('it')}>
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve (IT)
              </Button>
            </>
          )}
          {canImplement && (
            <Button onClick={() => setShowImplementModal(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Mark as Implemented
            </Button>
          )}
          {canRevoke && (
            <Button 
              variant="outline"
              className="text-orange-600 hover:text-orange-700"
              onClick={() => setShowRevokeModal(true)}
            >
              <Ban className="w-4 h-4 mr-2" />
              Revoke Access
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Form Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Access Request Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Employee Info */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Employee
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    {form.employee ? (
                      <>
                        <p className="font-medium">
                          {form.employee.firstName} {form.employee.lastName}
                        </p>
                        <p className="text-sm text-muted-foreground">{form.employee.email}</p>
                      </>
                    ) : (
                      <p className="text-muted-foreground">Not specified</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                    <Building className="w-4 h-4" />
                    Job Role
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-medium">{form.jobRole || 'Not specified'}</p>
                  </div>
                </div>
              </div>

              {/* Access Type & Level */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Access Type</h4>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <span className={cn(
                      "px-2 py-1 text-xs font-medium rounded-full",
                      form.accessType === 'NEW_HIRE' ? 'bg-blue-100 text-blue-800' :
                      form.accessType === 'ROLE_CHANGE' ? 'bg-purple-100 text-purple-800' :
                      form.accessType === 'TEMPORARY' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-orange-100 text-orange-800'
                    )}>
                      {form.accessType.replace(/_/g, ' ')}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    Access Level
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <span className={cn(
                      "px-2 py-1 text-xs font-medium rounded-full",
                      form.accessLevel === 'ADMIN' ? 'bg-red-100 text-red-800' :
                      form.accessLevel === 'WRITE' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    )}>
                      {form.accessLevel}
                    </span>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Start Date
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-medium">
                      {form.startDate ? formatDate(form.startDate) : 'Not specified'}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    End Date
                  </h4>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="font-medium">
                      {form.isPermanent ? 'Permanent' : form.endDate ? formatDate(form.endDate) : 'Not specified'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Systems Requested */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground">Systems Requested</h4>
                <div className="p-3 bg-muted/50 rounded-lg">
                  {systemsRequested.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {systemsRequested.map((system: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-primary/10 text-primary text-sm rounded-full"
                        >
                          {system}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No systems specified</p>
                  )}
                </div>
              </div>

              {/* Justification */}
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground">Justification</h4>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="whitespace-pre-wrap">{form.justification || 'No justification provided'}</p>
                </div>
              </div>

              {/* Notes */}
              {form.notes && (
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Additional Notes</h4>
                  <div className="p-3 bg-muted/50 rounded-lg">
                    <p className="whitespace-pre-wrap">{form.notes}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Approval Details */}
          <ApprovalDetailsCard form={form} />
        </div>

        {/* Sidebar - Workflow */}
        <div className="lg:col-span-1">
          <WorkflowVisualization form={form} />
        </div>
      </div>

      {/* Modals */}
      {showApprovalModal && (
        <ApprovalModal
          isOpen={true}
          onClose={() => setShowApprovalModal(null)}
          formId={id!}
          type={showApprovalModal}
          onSuccess={() => {
            setShowApprovalModal(null);
            refetch();
          }}
        />
      )}

      {showRejectModal && (
        <RejectModal
          isOpen={true}
          onClose={() => setShowRejectModal(null)}
          formId={id!}
          type={showRejectModal}
          onSuccess={() => {
            setShowRejectModal(null);
            refetch();
          }}
        />
      )}

      {showImplementModal && (
        <ImplementModal
          isOpen={true}
          onClose={() => setShowImplementModal(false)}
          formId={id!}
          onSuccess={() => {
            setShowImplementModal(false);
            refetch();
          }}
        />
      )}

      {showRevokeModal && (
        <RevokeModal
          isOpen={true}
          onClose={() => setShowRevokeModal(false)}
          formId={id!}
          onSuccess={() => {
            setShowRevokeModal(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}
